import type { ComponentType } from 'react';
import type { SourceType } from '@casetrace/shared';
import { FileArchive, FileText, Mail, type LucideProps } from 'lucide-react';

function Microsoft365Icon({ size = 14, className, ...props }: LucideProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <rect x="2" y="2" width="9" height="9" rx="1.5" fill="#f25022" />
      <rect x="13" y="2" width="9" height="9" rx="1.5" fill="#7fba00" />
      <rect x="2" y="13" width="9" height="9" rx="1.5" fill="#00a4ef" />
      <rect x="13" y="13" width="9" height="9" rx="1.5" fill="#ffb900" />
    </svg>
  );
}

function WhatsappColorIcon({ size = 14, className, ...props }: LucideProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <circle cx="12" cy="12" r="10" fill="#25D366" />
      <path
        d="M7.6 18.2l.8-3.1a6 6 0 1 1 2.7 2.5l-3.5.6z"
        stroke="#ffffff"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M10 10c.2-.5.5-.5.7-.5h.5c.2 0 .4 0 .5.3l.6 1.3c.1.2.1.4 0 .5l-.5.6c-.1.1-.1.3 0 .4.2.3.5.7.9 1 .4.3.8.6 1.2.8.1.1.3 0 .4-.1l.6-.6c.1-.1.3-.2.5-.1l1.3.6c.2.1.3.2.3.5v.5c0 .3 0 .5-.5.7-.4.2-1 .3-1.5.2-1.2-.2-2.4-.9-3.4-1.8-1-.9-1.7-2-1.9-3.3-.1-.6 0-1.1.3-1.5z"
        fill="#ffffff"
      />
    </svg>
  );
}

function OutlookColorIcon({ size = 14, className, ...props }: LucideProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <rect x="2" y="5" width="11" height="14" rx="2" fill="#0A64C5" />
      <rect x="9" y="4" width="13" height="16" rx="2" fill="#1976D2" />
      <path d="M9 8.5l6.5 4.8L22 8.5" stroke="#E8F2FF" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="7.5" cy="12" r="2.1" stroke="#E8F2FF" strokeWidth="1.4" />
    </svg>
  );
}

function IosAppleIcon({ size = 14, className, ...props }: LucideProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <rect x="1.5" y="1.5" width="21" height="21" rx="6" fill="#111318" stroke="#3a3d46" />
      <g transform="translate(4 3)">
        <path
          d="M12.2 9.7c0-2 1.6-3 1.7-3.1-1-1.5-2.7-1.7-3.2-1.7-1.4-.1-2.7.8-3.4.8-.7 0-1.8-.8-3-.8-1.6 0-3 1-3.8 2.3-1.6 2.7-.4 6.7 1.1 8.9.8 1 1.7 2.2 2.9 2.2 1.2 0 1.6-.7 3-.7 1.4 0 1.8.7 3 .7 1.3 0 2.1-1.1 2.8-2.1.9-1.3 1.3-2.6 1.3-2.7 0 0-2.4-.9-2.4-3.8z"
          fill="#ededf0"
        />
        <path
          d="M9.9 2.1c.6-.7 1-1.7.8-2.7-.9 0-1.9.6-2.5 1.3-.6.7-1.1 1.7-.9 2.7 1 .1 2-.5 2.6-1.3z"
          fill="#ededf0"
        />
      </g>
    </svg>
  );
}

const SOURCE_ICON_BY_TYPE = {
  m365: Microsoft365Icon,
  whatsapp: WhatsappColorIcon,
  gmail: Mail,
  imap: Mail,
  pst: OutlookColorIcon,
  mbox: FileArchive,
  eml: Mail,
  sms_backup: IosAppleIcon,
  iphone_backup: IosAppleIcon,
  document_upload: FileText,
} as const satisfies Record<SourceType, ComponentType<LucideProps>>;

type SourceTypeIconProps = {
  readonly type: SourceType;
  readonly className?: string;
  readonly size?: number;
};

export function SourceTypeIcon({ type, className, size = 14 }: SourceTypeIconProps) {
  const Icon = SOURCE_ICON_BY_TYPE[type];
  return <Icon size={size} className={className} aria-hidden="true" />;
}
